import React, { useState } from 'react';
import { X, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { stcBinSizeService, CreateStcBinSizeRequest } from '../../../../../services/stcBinSizeService';
import { useAuth } from '../../../../../contexts/AuthContext';

interface BinSizeCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const BinSizeCreateModal: React.FC<BinSizeCreateModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const { user } = useAuth();

    const [formData, setFormData] = useState<CreateStcBinSizeRequest>({
        companyNo: user?.companyNo || '001',
        binSizeNo: 1,
        description1: '',
        width: 0,
        depth: 0,
        height: 0,
        totalVolume: 0,
        unlimitedYesNo: false,
        externalSystemID: undefined
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.binSizeNo || formData.binSizeNo < 1) {
            setError('Størrelse nr. er obligatorisk');
            return;
        }

        if (!formData.description1.trim()) {
            setError('Beskrivelse er obligatorisk');
            return;
        }

        try {
            setIsSaving(true);
            setError('');
            await stcBinSizeService.create(formData);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSuccess();
            }, 1200);
        } catch (err: any) {
            setError(err.message || 'Feil ved opprettelse av lokasjonsstørrelse');
        } finally {
            setIsSaving(false);
        }
    };

    const updateField = <K extends keyof CreateStcBinSizeRequest>(key: K, value: CreateStcBinSizeRequest[K]) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">Ny lokasjonsstørrelse</h2>
                            <p className="text-sm text-green-100">Opprett en ny lokasjonsstørrelse</p>
                        </div>
                        <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">Lokasjonsstørrelse opprettet!</p>
                                </div>
                            </div>
                        )}

                        {/* Grunndata */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">Grunndata</legend>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Størrelse nr. <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.binSizeNo}
                                        onChange={(e) => updateField('binSizeNo', parseInt(e.target.value) || 1)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        required
                                        min="1"
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Beskrivelse <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.description1 || ''}
                                        onChange={(e) => updateField('description1', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="Beskrivelse av lokasjonsstørrelsen"
                                        maxLength={100}
                                        required
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Dimensjoner */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">Dimensjoner</legend>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Bredde</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.width}
                                        onChange={(e) => updateField('width', parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Dybde</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.depth}
                                        onChange={(e) => updateField('depth', parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Høyde</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.height}
                                        onChange={(e) => updateField('height', parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Totalt volum</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.totalVolume}
                                        onChange={(e) => updateField('totalVolume', parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Innstillinger */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">Innstillinger</legend>
                            <div className="grid grid-cols-2 gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.unlimitedYesNo}
                                        onChange={(e) => updateField('unlimitedYesNo', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">Ubegrenset</span>
                                </label>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Ekstern system ID</label>
                                    <input
                                        type="number"
                                        value={formData.externalSystemID ?? ''}
                                        onChange={(e) => updateField('externalSystemID', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Avbryt
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Oppretter...
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        Opprett lokasjonsstørrelse
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
